import { useState } from 'react';
import { STATUS_LABEL, TILE_LABEL } from '@helpdesk/shared';
import { postJson } from '../api/client.ts';
import type { RecentTicket } from '../api/types.ts';
import { fmtAge } from '../lib/format.ts';
import { PriorityBadge } from './badges.tsx';
import { Button, ErrorNote, Input, Notice, Textarea } from './ui.tsx';

/**
 * „Schon gemeldet? Hier anhängen“ (F19): offene Tickets derselben Maschine – nur
 * Nummer, Priorität, Status, Alter. Kein Freitext, kein Name.
 */
export function DuplicateHint({
  tickets,
  code,
  anonymous,
  kioskToken,
  sharedDevice = false,
}: {
  tickets: RecentTicket[];
  code?: string;
  anonymous: boolean;
  kioskToken?: string;
  sharedDevice?: boolean;
}) {
  const open = tickets.filter((t) => t.isOpen);
  const [target, setTarget] = useState<string | null>(null);
  const [text, setText] = useState('');
  const [name, setName] = useState('');
  const [done, setDone] = useState<string | null>(null);
  const [error, setError] = useState<unknown>(null);
  if (!open.length) return null;

  const send = async (t: RecentTicket) => {
    setError(null);
    try {
      if (kioskToken) await postJson(`/api/kiosk/${kioskToken}/tickets/${t.id}/comments`, { name, body: text, website: '' });
      else await postJson(`/api/quick/tickets/${t.id}/comments`, { code, name: anonymous ? name : 'angemeldet', body: text, website: '' });
      setDone(t.number);
      setTarget(null);
    } catch (err) {
      setError(err);
    }
  };

  return (
    <div className="border-l-4 border-warn bg-warn-tint px-4 py-3">
      <p className="font-semibold text-steel-900">Schon gemeldet?</p>
      <p className="mb-2 text-sm text-steel-800">An dieser Maschine ist bereits offen:</p>
      {done ? <Notice tone="ok">Hinweis an {done} angehängt. Danke!</Notice> : null}
      <ul className="space-y-2">
        {open.map((t) => (
          <li key={t.id} className="bg-white p-3">
            <div className="flex flex-wrap items-center gap-2">
              <span className="font-mono">{t.number}</span>
              <PriorityBadge priority={t.priority} />
              <span className="text-sm">{t.needsReview ? 'gemeldet, ungeprüft' : STATUS_LABEL[t.status]}</span>
              <span className="text-[13px] text-[var(--text-muted)]">{fmtAge(t.createdAt)}</span>
              {target !== t.id ? (
                <Button variant="outline" size="sm" className="ml-auto" onClick={() => setTarget(t.id)}>
                  Hier anhängen
                </Button>
              ) : null}
            </div>
            {target === t.id ? (
              <div className="mt-3 space-y-3">
                <Textarea label="Hinweis" value={text} onChange={setText} rows={2} maxLength={500} autoFocus />
                {anonymous ? <Input label="Ihr Name" value={name} onChange={setName} required autoComplete={kioskToken || sharedDevice ? 'off' : 'name'} /> : null}
                {error ? <ErrorNote error={error} /> : null}
                <div className="flex gap-2">
                  <Button onClick={() => void send(t)} disabled={text.trim().length < 2 || (anonymous && name.trim().length < 2)}>
                    Anhängen
                  </Button>
                  <Button variant="ghost" onClick={() => setTarget(null)}>
                    Abbrechen
                  </Button>
                </div>
              </div>
            ) : null}
          </li>
        ))}
      </ul>
      <p className="mt-2 text-[13px] text-steel-700">Etwas anderes? Dann unten neu melden ({Object.values(TILE_LABEL).join(', ')}).</p>
    </div>
  );
}
